import { useState, useEffect, useCallback, useRef } from "react";
import { readFromStorageCache, writeToStorageCache } from "../lib/storageCache";

export interface AsyncDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UseAsyncDataOptions {
  /**
   * When set, pre-populates `data` from localStorage on mount (no loading spinner if cache
   * hits) and writes fresh results back after each successful fetch — stale-while-revalidate.
   * The background fetch always runs; cache only eliminates the visible loading state.
   * Scope the key to the user (`rc_<resource>_${userId}`) to prevent cross-user bleed.
   */
  cacheKey?: string;
}

/**
 * Runs `fn` on mount and on every `refresh()` call.
 * Manages loading/error/data lifecycle. `fn` is stored in a ref so callers
 * do not need to memoize it — the latest version is always used.
 *
 * Pass `{ cacheKey }` to enable stale-while-revalidate: the cached value renders
 * immediately with no loading spinner; a background fetch updates it and writes
 * the fresh result back to the cache.
 */
export function useAsyncData<T>(
  fn: () => Promise<T>,
  options?: UseAsyncDataOptions
): AsyncDataResult<T> {
  const cacheKey = options?.cacheKey ?? null;

  const [data, setData] = useState<T | null>(() =>
    cacheKey ? readFromStorageCache<T>(cacheKey) : null
  );
  const hasDataRef = useRef(data !== null);
  const [loading, setLoading] = useState(!hasDataRef.current);
  const [error, setError] = useState<string | null>(null);

  const fnRef = useRef(fn);
  fnRef.current = fn;

  const refresh = useCallback(async () => {
    setError(null);
    if (!hasDataRef.current) setLoading(true);
    try {
      const fresh = await fnRef.current();
      hasDataRef.current = true;
      setData(fresh);
      if (cacheKey) writeToStorageCache(cacheKey, fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [cacheKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
